import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useWebmaster } from "context/admin/webmaster";

import Table from "components/Table";
import PillGridNew from "components/PillGridNew";
import TaskInfo from "components/TaskInfo";

import task_status_data from "libs/task_status_data.json";
import task_priority_data from "libs/task_priority_data.json";
import toHoursAndMinutes from "libs/helpers/toHoursAndMinutes";

const mainClass = "webmasters-admin-details";

const TasksTable = ({ setWebsiteInfoId, setExplanation }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, loading, filters, filterHandler, updateTask } = useWebmaster();

  const tableData = {
    keys: [
      { key: "created_at", value: t("Creation date") },
      { key: "website", value: t("Website [SHORT]") },
      { sort_key: "title", key: "title-full", value: t("Title") },
      { key: "status", value: t("Status") },
      { key: "priority", value: t("Priority") },
      { sort_key: "total_time", key: "time_tracked", value: t("Work time") },
      { key: "details", sort_key: false, value: t("Details") },
    ],
    items: data?.tasks?.map(
      ({
        _id,
        created_at,
        priority,
        title,
        website,
        website_id,
        total_time,
        status,
      }) => {
        const statusData = task_status_data?.find(
          (item) => item?.value === status,
        );
        const priorityData = task_priority_data?.find(
          (item) => item?.value === priority,
        );
        const isCompletelyDisabled = statusData?.key === "canceled";

        return [
          {
            key: "created_at",
            type: "text",
            value: moment(created_at).format("LL"),
          },
          {
            key: "website",
            type: "clickable_text",
            value: {
              label: website,
              icon: false,
              onclick: () => setWebsiteInfoId(website_id),
            },
          },
          {
            key: "title-full",
            type: "link",
            value: { label: title, href: `/tasks/${_id}` },
          },
          {
            key: "status",
            type: "pill",
            value: {
              data: {
                value: statusData?.value,
                label: t(statusData?.label),
                color: statusData?.color,
              },
              options: !isCompletelyDisabled
                ? task_status_data.map((item) => ({
                    ...item,
                    label: t(item.label),
                  }))
                : [],
              optionsFooter: !isCompletelyDisabled ? (
                <TaskInfo onClick={() => setExplanation("status")} />
              ) : null,
              isCompletelyDisabled,
              isCompact: true,
              onChange: !isCompletelyDisabled
                ? (option) => updateTask(_id, { status: option.value })
                : undefined,
            },
          },
          {
            key: "priority",
            type: "pill",
            value: {
              data: {
                value: priorityData?.value,
                label: t(priorityData?.label),
                color: priorityData?.color,
              },
              icon: "flag",
              options: !isCompletelyDisabled
                ? task_priority_data.map((item) => ({
                    ...item,
                    label: t(item.label),
                  }))
                : [],
              optionsFooter: !isCompletelyDisabled ? (
                <TaskInfo
                  type="priority"
                  onClick={() => setExplanation("priority")}
                />
              ) : null,
              isCompletelyDisabled,
              isCompact: true,
              onChange: !isCompletelyDisabled
                ? (option) => updateTask(_id, { priority: option.value })
                : undefined,
            },
          },
          {
            key: "time_tracked",
            value: total_time?.$numberDecimal
              ? toHoursAndMinutes(total_time.$numberDecimal)?.formatted
              : "-",
          },
          {
            key: "details",
            type: "clickable_text",
            value: {
              label: t("View"),
              onclick: () => navigate(`/tasks/${_id}`),
            },
          },
        ];
      },
    ),
  };

  return (
    <Table
      data={tableData}
      loading={loading}
      sort={filters?.sort}
      setSort={(value) => filterHandler("sort", value)}
      filters_new={
        <div className={`${mainClass}__filters`}>
          <div>
            <PillGridNew
              header="Status"
              label={t("Select status")}
              selected={filters?.status}
              options={task_status_data
                .filter(
                  ({ key }) =>
                    !["client_verification", "closed", "canceled"].includes(
                      key,
                    ),
                )
                .map((item) => ({ ...item, label: t(item.label) }))}
              optionsFooter={
                <TaskInfo onClick={() => setExplanation("status")} />
              }
              onChange={(option) =>
                filterHandler("status", option.key, true)
              }
            />
          </div>
          <div>
            <PillGridNew
              header={t("Priority")}
              label={t("Select priority")}
              selected={filters?.priority}
              icon="flag"
              options={task_priority_data.map((item) => ({
                ...item,
                label: t(item.label),
              }))}
              optionsFooter={
                <TaskInfo
                  type="priority"
                  onClick={() => setExplanation("priority")}
                />
              }
              onChange={(option) =>
                filterHandler("priority", option.key, true)
              }
            />
          </div>
        </div>
      }
    />
  );
};

export default TasksTable;